<script setup lang="ts" generic="Row extends object">
import { computed, h, ref, unref, watch } from 'vue'
import { NDataTable, NScrollbar } from 'naive-ui'
import type {
  DataTableBaseColumn,
  DataTableColumns,
  DataTableInst,
  DataTableSortOrder,
  DataTableSortState,
  ScrollbarInst,
} from 'naive-ui'
import emptyImage from '@/assets/empty.svg'
import type { DataListPagination, DataListTableData } from './types'
import TableColumnSettings from './TableColumnSettings.vue'
import { summaryColumnKeys, useTableColumns } from './useTableColumns'

const DEFAULT_PAGE_SLOT = 5
const DEFAULT_PAGE_SIZES = [10, 20, 50]
const showDefaultTotal = (total: number) => `共 ${total} 条`

/**
 * 为未自行定制排序器的列渲染统一的双向排序图标。
 *
 * @param order Naive UI 当前排序状态：升序、降序或未排序。
 */
function renderDefaultSorterIcon({ order }: { order: DataTableSortOrder }) {
  return h(
    'span',
    {
      class: [
        'data-list-table__sorter-icon',
        order === 'ascend' && 'data-list-table__sorter-icon--asc',
        order === 'descend' && 'data-list-table__sorter-icon--desc',
      ],
      'aria-hidden': 'true',
    },
    [
      h('span', { class: 'data-list-table__sorter-caret data-list-table__sorter-caret--asc' }),
      h('span', { class: 'data-list-table__sorter-caret data-list-table__sorter-caret--desc' }),
    ],
  )
}

const props = withDefaults(
  defineProps<{
    data: DataListTableData<Row>
    isBorder?: boolean
    emptyText?: string
    /** 固定且唯一的表格别名，用于隔离本地列设置。 */
    tableKey?: string
  }>(),
  { isBorder: true, emptyText: '暂无数据' },
)

const emit = defineEmits<{
  change: [pagination: DataListPagination]
  expandedRowKeysChange: [keys: Array<string | number>]
  sorterChange: [sorter: DataTableSortState | DataTableSortState[] | null]
}>()

/**
 * 补齐数据列的公共默认配置，确保顶层列和分组表头的叶子列使用同款排序图标。
 *
 * @param column Naive UI 的可展示数据列配置。
 * @returns 保留原列配置并补齐默认值后的新列对象。
 */
function normalizeDataTableColumn(column: DataTableBaseColumn<Row>) {
  const item = { ...column }
  if ((item.key === 'action' || item.key === 'actions') && !item.fixed) item.fixed = 'right'
  if (item.sorter && !item.renderSorter && !item.renderSorterIcon) {
    item.renderSorterIcon = renderDefaultSorterIcon
  }
  return item
}

const baseColumns = computed<DataTableColumns<Row>>(() =>
  unref(props.data.columns).map((column) => {
    if ('children' in column) {
      return {
        ...column,
        children: column.children.map(normalizeDataTableColumn),
      }
    }
    return normalizeDataTableColumn(column as DataTableBaseColumn<Row>)
  }),
)

const rows = computed(() => unref(props.data.dataSource))
const summary = computed(() => props.data.summary?.(rows.value))
const columnSettings = useTableColumns(
  baseColumns,
  () => props.tableKey,
  () => summaryColumnKeys(baseColumns.value, summary.value),
)
const {
  columns,
  enabled: settingsEnabled,
  options: columnOptions,
  preference,
  storageKey,
} = columnSettings
const tableScrollX = computed(() => columnSettings.width.value ?? props.data.scrollX)
const stickyHorizontalScrollbar = computed(() => props.data.stickyHorizontalScrollbar === true)
const tableRef = ref<DataTableInst | null>(null)
const tableRootRef = ref<HTMLElement | null>(null)
const scrollbarRef = ref<ScrollbarInst | null>(null)
const viewportWidth = ref(0)
const horizontalContentWidth = computed(() => {
  const width = Number(tableScrollX.value || 0)
  return Number.isFinite(width) ? width : 0
})
const hasHorizontalScroll = computed(
  () => stickyHorizontalScrollbar.value && horizontalContentWidth.value > viewportWidth.value + 1,
)
// 滚动位置只用于双向同步，不参与表格渲染。
let syncedScrollLeft = 0

/** 将表格触控板滚动同步到底部滚动条，并保留页面传入的 onScroll 回调。 */
function handleTableScroll(event: Event) {
  props.data.onScroll?.(event)
  const source = (event.currentTarget || event.target) as HTMLElement | null
  const left = source?.scrollLeft || 0
  if (!scrollbarRef.value || Math.abs(left - syncedScrollLeft) <= 1) return
  syncedScrollLeft = left
  scrollbarRef.value.scrollTo({ left })
}

/** 将底部滚动条 event 的横向位置同步到 Naive UI 表格公开实例。 */
function handleStickyScrollbarScroll(event: Event) {
  const source = event.target as HTMLElement
  const left = source.scrollLeft
  if (Math.abs(left - syncedScrollLeft) <= 1) return
  syncedScrollLeft = left
  tableRef.value?.scrollTo({ left })
}

watch(
  [tableRootRef, stickyHorizontalScrollbar],
  ([root, enabled], _, onCleanup) => {
    if (!root || !enabled) {
      viewportWidth.value = 0
      return
    }

    /** 根据表格外框尺寸刷新底部滚动条可视宽度。 */
    const updateViewportWidth = () => {
      viewportWidth.value = root.clientWidth
    }

    const observer = new ResizeObserver(updateViewportWidth)
    observer.observe(root)
    updateViewportWidth()
    onCleanup(() => observer.disconnect())
  },
  { flush: 'post' },
)

watch(
  [viewportWidth, horizontalContentWidth, scrollbarRef],
  () => {
    if (!scrollbarRef.value || !stickyHorizontalScrollbar.value) return
    const maxScrollLeft = Math.max(0, horizontalContentWidth.value - viewportWidth.value)
    syncedScrollLeft = Math.min(syncedScrollLeft, maxScrollLeft)
    scrollbarRef.value.scrollTo({ left: syncedScrollLeft })
  },
  { flush: 'post' },
)

const tableProps = computed(() => {
  const {
    columns: _columns,
    dataSource: _dataSource,
    pagination: _pagination,
    stickyHorizontalScrollbar: _stickyHorizontalScrollbar,
    onScroll: _onScroll,
    ...rest
  } = props.data
  return {
    ...rest,
    scrollX: tableScrollX.value,
    onScroll: stickyHorizontalScrollbar.value ? handleTableScroll : _onScroll,
  }
})
const tableRemote = computed(() => props.data.remote ?? Boolean(props.data.pagination))

/** 将列表分页参数适配为 Naive UI 表格内置分页参数。 */
const tablePagination = computed(() => {
  const pagination = props.data.pagination
  if (!pagination) return false

  return {
    page: pagination.current,
    pageSize: pagination.pageSize,
    itemCount: pagination.total,
    pageSizes: pagination.pageSizes || DEFAULT_PAGE_SIZES,
    pageSlot: pagination.pageSlot ?? DEFAULT_PAGE_SLOT,
    showSizePicker: pagination.showSizePicker !== false,
    showQuickJumper: pagination.showQuickJumper !== false,
    prefix: () => (pagination.showTotal ?? showDefaultTotal)(pagination.total),
  }
})

/** 将分页变更统一交给页面更新请求参数和表格状态。 */
function handlePageChange(page: number) {
  if (!props.data.pagination) return
  emit('change', { ...props.data.pagination, current: page })
}

/** 修改每页条数时重置为第一页并统一派发分页状态。 */
function handlePageSizeChange(pageSize: number) {
  if (!props.data.pagination) return
  emit('change', { ...props.data.pagination, current: 1, pageSize })
}
</script>

<template>
  <section
    ref="tableRootRef"
    class="data-list-table"
    :class="{ 'data-list-table--sticky-horizontal': stickyHorizontalScrollbar }"
    :data-table-key="tableKey"
    :style="{ borderWidth: isBorder ? '1px' : '0px' }"
  >
    <slot name="header" />
    <TableColumnSettings
      v-if="settingsEnabled"
      :options="columnOptions"
      :preference="preference"
      :scope-key="storageKey"
      @apply="columnSettings.apply"
      @reset="columnSettings.reset"
    />
    <NDataTable
      ref="tableRef"
      v-bind="tableProps"
      :columns="columns"
      :data="rows"
      :bordered="data.bordered === true"
      :striped="data.striped === true"
      :remote="tableRemote"
      :pagination="tablePagination"
      @update:page="handlePageChange"
      @update:page-size="handlePageSizeChange"
      @update:sorter="emit('sorterChange', $event)"
      @update:expanded-row-keys="emit('expandedRowKeysChange', $event)"
    >
      <template #empty>
        <slot name="empty">
          <div
            class="data-list-table__empty"
            data-test="data-list-table-empty"
          >
            <img
              :src="emptyImage"
              alt=""
              class="data-list-table__empty-icon"
            >
            <span class="data-list-table__empty-text">{{ emptyText }}</span>
          </div>
        </slot>
      </template>
    </NDataTable>
    <div
      v-show="hasHorizontalScroll"
      class="data-list-table__sticky-scrollbar"
      tabindex="0"
      role="region"
      aria-label="表格横向滚动"
      @keydown.left.prevent="scrollbarRef?.scrollBy({ left: -80 })"
      @keydown.right.prevent="scrollbarRef?.scrollBy({ left: 80 })"
      @keydown.home.prevent="scrollbarRef?.scrollTo({ left: 0 })"
      @keydown.end.prevent="scrollbarRef?.scrollTo({ left: horizontalContentWidth })"
    >
      <NScrollbar
        ref="scrollbarRef"
        style="height: 100%"
        x-scrollable
        trigger="none"
        :theme-overrides="{ height: '8px', railInsetHorizontalBottom: '2px 0' }"
        @scroll="handleStickyScrollbarScroll"
      >
        <div :style="{ width: `${horizontalContentWidth}px`, height: '1px' }" />
      </NScrollbar>
    </div>
  </section>
</template>

<style scoped>
.data-list-table {
  overflow: hidden;
  border: 1px solid #e8e9ec;
  border-radius: 8px;
  background: #fff;
}

.data-list-table--sticky-horizontal {
  display: flex;
  min-height: 0;
  flex-direction: column;
  overflow: clip;
}

.data-list-table--sticky-horizontal :deep(.n-data-table--flex-height) {
  height: 0;
  min-height: 0;
  flex: 1 1 auto;
}

/* 吸附模式使用独立横向轨道，隐藏 Naive UI 数据区自带轨道，避免重复显示。 */
.data-list-table--sticky-horizontal
  :deep(.n-data-table-base-table-body > .n-scrollbar-rail--horizontal) {
  display: none;
}

.data-list-table__sticky-scrollbar {
  position: sticky;
  bottom: 0;
  z-index: 4;
  height: 12px;
  flex: none;
  margin-inline: 2px;
  contain: layout paint;
  background: #f8fafc;
}

.data-list-table :deep(.n-data-table-th),
.data-list-table :deep(.n-data-table-th--sortable:hover),
.data-list-table :deep(.n-data-table-th--sorting) {
  height: 45px;
  background: #f5f6f8;
  color: #313233;
  font-size: 13px;
  font-weight: 500;
}

.data-list-table :deep(.n-data-table-td) {
  height: 52px;
  color: #27364d;
  font-size: 13px;
  /* vertical-align: top; */
}

.data-list-table :deep(.n-data-table-sorter) {
  position: relative;
  width: 12px;
  height: 16px;
  flex: 0 0 12px;
  margin-left: 2px;
}

.data-list-table :deep(.data-list-table__sorter-caret) {
  position: absolute;
  left: 3px;
  width: 0;
  height: 0;
  border-right: 3px solid transparent;
  border-left: 3px solid transparent;
}

.data-list-table :deep(.data-list-table__sorter-caret--asc) {
  top: 2px;
  border-bottom: 4px solid #b6c2d0;
}

.data-list-table :deep(.data-list-table__sorter-caret--desc) {
  bottom: 2px;
  border-top: 4px solid #b6c2d0;
}

.data-list-table
  :deep(
    .n-data-table-th--sortable:not(.n-data-table-th--sorting):hover .data-list-table__sorter-caret
  ) {
  border-top-color: var(--color-primary, #2d7dff);
  border-bottom-color: var(--color-primary, #2d7dff);
}

.data-list-table :deep(.data-list-table__sorter-icon--asc .data-list-table__sorter-caret--asc) {
  border-bottom-color: var(--color-primary, #2d7dff);
}

.data-list-table :deep(.data-list-table__sorter-icon--desc .data-list-table__sorter-caret--desc) {
  border-top-color: var(--color-primary, #2d7dff);
}

.data-list-table :deep(.n-data-table-th:first-child),
.data-list-table :deep(.n-data-table-td:first-child) {
  padding-left: 24px;
}

.data-list-table :deep(.n-data-table-th:last-child),
.data-list-table :deep(.n-data-table-td:last-child) {
  padding-right: 24px;
}

.data-list-table :deep(.n-data-table-tr:hover .n-data-table-td) {
  background: #f7f9fc;
}

/* .data-list-table :deep(.n-data-table-th--fixed-right),
.data-list-table :deep(.n-data-table-td--fixed-right) {
  background: #fff;
} */

.data-list-table :deep(.n-data-table-tr:hover .n-data-table-td--fixed-right) {
  background: #f7f9fc;
}

.data-list-table :deep(.n-data-table__pagination) {
  padding: 5px 15px 15px;
}

.data-list-table :deep(.n-data-table__pagination .n-pagination) {
  width: 100%;
}

.data-list-table :deep(.n-data-table__pagination .n-pagination-prefix) {
  flex: 1;
  color: #64748b;
  font-size: 12px;
}

.data-list-table__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.data-list-table__empty-icon {
  width: 55px;
  height: 54px;
}

.data-list-table__empty-text {
  color: #ced7e3;
  font-size: 14px;
}
</style>
